// src/pages/Login.js
//
// Login de HolaVeci Local (POS). Habla contra POST /api/pos/auth/login.
// Acepta correo o teléfono como identificador.
//
// Comportamiento con sesión activa:
// Si al abrir /login ya hay un usuario logueado, NO se redirige a ciegas
// al dashboard. Se muestra un aviso indicando qué sesión hay activa y
// se ofrece: (a) ir al dashboard como ese usuario, o (b) cerrar sesión
// para entrar como otro. Esto evita el bug de "creí que hice login pero
// entré con la sesión anterior".

import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Alert, InputAdornment, IconButton,
  Divider, CircularProgress, Stack, Link,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../context/AuthContext';
import { BACKEND_ES_LOCAL } from '../api/apiClient';
import LogoHolaVeciLocal from '../components/marca/LogoHolaVeciLocal';

const Login = () => {
  const { login, logout, user } = useAuth();
  const navigate = useNavigate();
  const [usuario, setUsuario]      = useState('');
  const [password, setPassword]    = useState('');
  const [showPassword, setShowPwd] = useState(false);
  const [error, setError]          = useState('');
  const [enviando, setEnviando]    = useState(false);

  /** Validación mínima: correo con @ o teléfono con al menos 10 dígitos. */
  const validarUsuario = (raw) => {
    if (!raw) return 'Ingresa tu correo o teléfono.';
    const s = raw.trim();
    if (s.includes('@')) {
      if (s.length < 5 || !s.includes('.')) return 'Correo inválido.';
      return null;
    }
    const digits = s.replace(/\D/g, '');
    if (digits.length < 10) return 'Teléfono muy corto. Debe tener 10 dígitos (o pon un correo).';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const errU = validarUsuario(usuario);
    if (errU) { setError(errU); return; }
    if (!password || password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    setEnviando(true);
    try {
      // Si había una sesión activa, cerrarla antes para no dejar rastros.
      if (user) logout();
      const r = await login(usuario.trim(), password);
      if (r.ok) navigate('/dashboard', { replace: true });
      else setError(r.error);
    } finally {
      setEnviando(false);
    }
  };

  const cerrarYQuedarme = () => {
    logout();
    setUsuario('');
    setPassword('');
    setError('');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2,
        // Verde HolaVeci con el mismo degradado de la landing.
        background: 'linear-gradient(135deg, #083025 0%, #0F4C3A 55%, #2E6E5A 100%)',
      }}
    >
      <Paper elevation={0} sx={{
        p: { xs: 3, sm: 5 }, width: '100%', maxWidth: 440, borderRadius: 3,
        border: '1px solid rgba(15, 76, 58, 0.14)',
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3, textAlign: 'center' }}>
          <LogoHolaVeciLocal size={34} compacto />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Caja, inventario y ventas de tu negocio
          </Typography>
        </Box>

        {/* Aviso de sesión activa: no se auto-redirige. Se le da la elección
            al usuario para evitar entrar con una sesión distinta a la que
            se cree que se ha iniciado. */}
        {user && (
          <Alert severity="info" sx={{ mb: 2 }}
                 action={
                   <Stack direction="row" spacing={1}>
                     <Button size="small" onClick={() => navigate('/dashboard', { replace: true })}>
                       Continuar
                     </Button>
                     <Button size="small" color="warning" startIcon={<LogoutIcon />}
                             onClick={cerrarYQuedarme}>
                       Salir
                     </Button>
                   </Stack>
                 }>
            Ya tienes una sesión activa como <b>{user.nombre}</b> ({user.correo || user.usuario}).
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth label="Correo o teléfono" value={usuario}
            onChange={(e) => setUsuario(e.target.value)}
            margin="normal" autoFocus required autoComplete="username"
            placeholder="tucorreo@ejemplo.com o 3001234567"
            disabled={enviando}
          />
          <TextField
            fullWidth label="Contraseña" type={showPassword ? 'text' : 'password'}
            value={password} onChange={(e) => setPassword(e.target.value)}
            margin="normal" required autoComplete="current-password"
            disabled={enviando}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPwd((s) => !s)} edge="end" tabIndex={-1}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit" fullWidth variant="contained" size="large"
            sx={{ mt: 3, py: 1.3 }} disabled={enviando}
          >
            {enviando ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Iniciar sesión'}
          </Button>
        </form>

        <Typography variant="body2" align="center" sx={{ mt: 2.5 }}>
          ¿Tu negocio aún no tiene cuenta?{' '}
          <Link component={RouterLink} to="/registro" fontWeight={700}>
            Regístrate
          </Link>
        </Typography>

        <Divider sx={{ my: 3 }}>
          <Typography variant="caption" color="text.secondary">Info</Typography>
        </Divider>

        <Typography variant="caption" color="text.secondary" align="center"
                    sx={{ display: 'block', lineHeight: 1.6 }}>
          Ingresa con el correo o teléfono con el que se registró tu cuenta.
        </Typography>

        {/* Estado de conexión: no se muestra la URL del backend. */}
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75} sx={{ mt: 2 }}>
          <Box sx={{
            width: 8, height: 8, borderRadius: '50%',
            bgcolor: BACKEND_ES_LOCAL ? 'warning.main' : 'success.main',
          }} />
          <Typography variant="caption" sx={{
            fontSize: 11,
            color: BACKEND_ES_LOCAL ? 'warning.main' : 'text.secondary',
          }}>
            {BACKEND_ES_LOCAL ? 'localhost' : 'En línea'}
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
};

export default Login;
